import { useEffect, useSyncExternalStore } from 'react';

// THE live-drag channel between two Gantts showing the same thing (owner
// 2026-10-12: a drag in the Breakdown Timeline or in the gag panel's Task
// planning chart should move the other one LIVE, not after the save).
//
// A chart PUBLISHES the dates under its pointer while it drags (and while its
// confirm is open, so the other chart does not snap back before the answer),
// and every chart READS the channel for bars it is not itself dragging. It
// carries display positions only: nothing here is saved, gated or trusted —
// the dragging chart still owns the confirm and the request.
//
// Module state, not context: the two charts sit in different subtrees (the
// Timeline, and a panel several components below the page) and the shared
// drag hooks (`teams/CrossTeamGanttView.js`) are where both ends live.
// Keys: `ganttTaskLiveKey(id)` → `{ start, due }`; a gag date →
// `ganttGagDateLiveKey(gagId, field)` → `{ date }`.

let live = {};
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const snapshot = () => live;

export const ganttTaskLiveKey = (taskId) => `task:${taskId}`;
export const ganttGagDateLiveKey = (gagId, field) => `gag:${gagId}:${field}`;

/** Set (or, with a null value, clear) one key. A no-op when nothing changes. */
export function publishGanttLiveDrag(key, value) {
  if (!key) return;
  if (value == null) {
    if (!(key in live)) return;
    const { [key]: _gone, ...rest } = live;
    live = rest;
  } else {
    live = { ...live, [key]: value };
  }
  emit();
}

/** Every live drag, keyed. A new object on each change. */
export function useGanttLiveDrags() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Publish `value` under `key` while both are set; clears on change and unmount. */
export function usePublishGanttLiveDrag(key, value) {
  const sig = key && value ? JSON.stringify(value) : null;
  useEffect(() => {
    if (!key || !sig) return undefined;
    publishGanttLiveDrag(key, JSON.parse(sig));
    return () => publishGanttLiveDrag(key, null);
  }, [key, sig]);
}
